/**
 * Custom checkout — refresh sekcji "Sposób dostawy" po zmianie adresu.
 *
 * Custom checkout dzieli sekcje (address / shipping / payment) na osobne partials.
 * WC's `update_checkout` AJAX odświeża tylko `#order_review` — nasz shipping
 * section jest poza nim, więc wymaga osobnego refreshu.
 *
 * Listener (debounced 400ms) na zmianach `billing_country/postcode/city`,
 * `shipping_country/postcode/city` → POST do `/hn/v1/checkout/refresh-shipping`
 * → swap innerHTML `[data-hn-co-section="shipping"]`.
 *
 * Dodatkowo triggeruje WC `update_checkout` żeby totale + payment methods
 * w `#order_review` też się odświeżyły (jeśli WC's checkout.js jest aktywne).
 */
export function initCheckoutShippingRefresh() {
    if (typeof window.HN_CHECKOUT === 'undefined' || !window.HN_CHECKOUT.restShipping) {
        return;
    }
    const cfg = window.HN_CHECKOUT;
    if (!document.querySelector('[data-hn-co-section="shipping"]')) return;
    /* Helper — query za każdym razem. Po `replaceWith` w refresh() oryginalny
     * element jest detached, więc trzymanie referencji w closure powoduje że
     * druga próba swap'a nic nie robi (bug: pierwszy country change działa,
     * drugi nie). */
    const getSection = () => document.querySelector('[data-hn-co-section="shipping"]');

    // Toggle widoczności InPost Paczkomat wrapper'a (`#easypack_show_geowidget` button
    // + hidden inputs `parcel_machine_id`/`_desc`) — pokazujemy gdy wybrana metoda ma
    // `data-is-paczkomat="1"` (label zawiera "Paczkomat"), ukrywamy w przeciwnym razie.
    //
    // Plugin InPost (`inpost-pl.js`) bindiuje swój jBox modal na `#easypack_show_geowidget`
    // na document.ready — wystarczy że ten button istnieje w DOM (niekoniecznie widoczny).
    const syncInpostWrap = () => {
        const wrap = document.querySelector('[data-hn-co-inpost-wrap]');
        if (!wrap) return;
        const checked = document.querySelector('input.shipping_method:checked');
        const show = !!(checked && checked.dataset.isPaczkomat === '1');
        wrap.style.display = show ? '' : 'none';
    };
    syncInpostWrap();

    // Live-toggle `.is-selected` na karcie kuriera + AJAX do naszego endpointu
    // który ustawia chosen_shipping_methods w sesji i zwraca świeży HTML
    // section-shipping + order-review.
    //
    // Nie polegamy na WC `update_checkout` event (nasz custom layout ma radio
    // w formie, ale WC checkout.js bywa nie-zbindowany na czas — race condition
    // przy fragmentach). Robimy własny round-trip — deterministyczny.
    document.addEventListener('change', async (e) => {
        const radio = e.target;
        if (!radio || !radio.matches('input.shipping_method')) return;
        const list = radio.closest('[data-package-index]');
        if (!list) return;
        list.querySelectorAll('.hn-co__shipping-card').forEach((card) => {
            card.classList.remove('is-selected');
        });
        const card = radio.closest('.hn-co__shipping-card');
        if (card) card.classList.add('is-selected');

        // Sync InPost wrapper visibility — show button "Wybierz Paczkomat" gdy paczkomat zaznaczony.
        syncInpostWrap();

        // POST do naszego endpointu z wybraną metodą.
        const review = document.getElementById('order_review');
        if (review) review.classList.add('processing');
        try {
            const addr = readAddress();
            const fd = new FormData();
            fd.append('country', addr.country);
            fd.append('postcode', addr.postcode);
            fd.append('city', addr.city);
            fd.append('shipping_method', radio.value);
            const res = await fetch(cfg.restShipping, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': cfg.nonce, 'Accept': 'application/json' },
                body: fd,
            });
            if (res.ok) {
                const data = await res.json();
                // Swap order-review (totale + shipping cost + grand total).
                if (data && data.review && review) {
                    review.innerHTML = data.review;
                }
                // Swap section-payment — dostępne metody płatności mogą się zmienić
                // po wyborze shipping (np. "za pobraniem" shipping → tylko COD payment
                // przez filter w inc/shipping-payment-rules.php).
                swapPaymentSection(data);
                // Update sticky CTA totals i mobile summary total z fragments.
                if (data && data.fragments && data.fragments.total) {
                    document.querySelectorAll('[data-hn-co-submit-total], [data-hn-co-mobile-total], [data-hn-co-grand-total]').forEach((el) => {
                        el.innerHTML = data.fragments.total;
                    });
                }
            }
        } catch (_) {
            // Cicho — failure nie wywala checkoutu.
        } finally {
            if (review) review.classList.remove('processing');
        }
    });

    /**
     * Swap section-payment z fresh PHP rendera (uwzględnia aktualny shipping →
     * payment filter rules). Helper żeby reuse'ować w obu handlerach.
     */
    function swapPaymentSection(data) {
        if (!data || !data.payment) return;
        const paymentSection = document.querySelector('[data-hn-co-section="payment"]');
        if (!paymentSection) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = data.payment;
        const fresh = tmp.querySelector('[data-hn-co-section="payment"]');
        if (fresh) {
            paymentSection.replaceWith(fresh);
        }
    }

    // Fields które wpływają na shipping rates (zarówno billing jak i shipping).
    const watchSelectors = [
        'select[name="billing_country"]',
        'input[name="billing_postcode"]',
        'input[name="billing_city"]',
        'select[name="shipping_country"]',
        'input[name="shipping_postcode"]',
        'input[name="shipping_city"]',
    ];

    let lastSig = '';
    let timer = null;
    let inFlight = false;

    const readAddress = () => {
        // Preferuj shipping_* gdy "ship to different address" zaznaczone,
        // inaczej weź billing_*.
        const ship = document.getElementById('ship_to_different_address');
        const prefix = (ship && ship.checked) ? 'shipping' : 'billing';
        const get = (name) => {
            const el = document.querySelector(`[name="${name}"]`);
            return el ? el.value.trim() : '';
        };
        return {
            country:  get(`${prefix}_country`)  || 'PL',
            postcode: get(`${prefix}_postcode`),
            city:     get(`${prefix}_city`),
        };
    };

    const refresh = async () => {
        const addr = readAddress();
        // Sygnatura — pomijamy gdy nic się nie zmieniło.
        const sig = `${addr.country}|${addr.postcode}|${addr.city}`;
        if (sig === lastSig) return;
        lastSig = sig;
        // (Wcześniej pomijaliśmy gdy brak postcode+city; usunięte — country change
        // sam w sobie wymaga refresh'a, bo strefy WC matchują po kraju + ewentualnie postcode.)

        if (inFlight) return;
        inFlight = true;
        const sectionAtStart = getSection();
        if (sectionAtStart) sectionAtStart.classList.add('is-loading');

        try {
            const fd = new FormData();
            fd.append('country', addr.country);
            fd.append('postcode', addr.postcode);
            fd.append('city', addr.city);

            const res = await fetch(cfg.restShipping, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': cfg.nonce, 'Accept': 'application/json' },
                body: fd,
            });
            if (res.ok) {
                const data = await res.json();
                if (data && data.html) {
                    // Swap zachowując sam wrapper section (parent contains data-hn-co-section).
                    // Backend renderuje całą sekcję — zastępujemy outer.
                    const tmp = document.createElement('div');
                    tmp.innerHTML = data.html;
                    const fresh = tmp.querySelector('[data-hn-co-section="shipping"]');
                    const current = getSection();
                    if (fresh && current) {
                        current.replaceWith(fresh);
                    } else if (current) {
                        current.innerHTML = data.html;
                    }
                }
                // Swap section-payment — po zmianie adresu dostępne metody dostawy
                // mogą się zmienić → cascade do dostępnych płatności (jeśli nowa
                // domyślnie wybrana dostawa to COD, payment list pokazuje tylko COD).
                swapPaymentSection(data);
                // Swap order-review (totale).
                const review = document.getElementById('order_review');
                if (review && data && data.review) {
                    review.innerHTML = data.review;
                }
            }

            // Notify WC update_checkout — odświeży #order_review (totals + payment).
            // Plus updated_shipping_method — InPost / Furgonetka pluginy nasłuchują
            // tego eventu żeby re-init map widget / picker UI po AJAX swap'ie.
            // Trigger change na aktualnie wybranym radio — wymusza re-binding handlerów
            // pluginów które używają delegacji na `.shipping_method`.
            if (window.jQuery) {
                const $ = window.jQuery;
                $(document.body).trigger('update_checkout');
                $(document.body).trigger('updated_shipping_method');
                const $checked = $('input.shipping_method:checked');
                if ($checked.length) {
                    $checked.trigger('change');
                }
            }
            // Sync InPost wrapper visibility po swap'ie (fresh DOM).
            syncInpostWrap();
        } catch (_) {
            // Cicho — failure nie wywala checkoutu.
        } finally {
            inFlight = false;
            /* Re-query — `getSection()` zwraca aktualny DOM node (po potencjalnym swap'ie).
             * Dodatkowo czyścimy klasę na starym sectionAtStart na wypadek error path'a
             * w którym swap się nie odbył. */
            const next = getSection();
            if (next) next.classList.remove('is-loading');
            if (sectionAtStart && sectionAtStart !== next) sectionAtStart.classList.remove('is-loading');
        }
    };

    const debounced = () => {
        clearTimeout(timer);
        timer = setTimeout(refresh, 400);
    };

    // Delegacja — formularz przeładowuje pola po replaceWith / update_checkout,
    // więc bind na document zamiast na konkretnych inputach.
    document.addEventListener('change', (e) => {
        if (watchSelectors.some((sel) => e.target.matches(sel))) debounced();
    });
    document.addEventListener('blur', (e) => {
        if (watchSelectors.some((sel) => e.target.matches(sel))) debounced();
    }, true); // capture = blur events bubble nie domyślnie

    /* COUNTRY CHANGE — direct explicit binding + immediate refresh.
     *
     * Powód: delegacja `document.addEventListener('change')` czasami nie łapie
     * country selecta gdy:
     *  - WC selectWoo wrappa <select> w custom dropdown (event nie bubble'uje
     *    standardowo do document w niektórych przeglądarkach)
     *  - Inny moduł zatrzymał propagation w capture phase
     *  - Bind był wykonany przed renderowaniem selecta (race condition)
     *
     * Direct bind + immediate refresh (no 400ms debounce — country zmieniany
     * świadomie, nie podczas typing'u). Plus reset `lastSig` żeby kolejny refresh
     * zawsze przeszedł nawet jeśli kraj był już raz testowany. */
    const bindCountry = (id) => {
        const sel = document.getElementById(id);
        if (!sel || sel.dataset.hnCountryBound === '1') return;
        sel.dataset.hnCountryBound = '1';
        sel.addEventListener('change', () => {
            lastSig = ''; // wymuś refresh nawet jeśli sig nie wygląda na zmieniony
            clearTimeout(timer);
            refresh(); // immediate, no debounce
        });
    };
    bindCountry('billing_country');
    bindCountry('shipping_country');

    /* Re-bind po każdym swap'ie section-address (jeśli kiedyś będzie hot-replace'owany).
     * Plus: po `ship_to_different_address` toggle, shipping_country pojawia się
     * w DOM dopiero gdy user otworzy panel — re-bind. */
    const reBindOnToggle = document.getElementById('ship_to_different_address');
    if (reBindOnToggle) {
        reBindOnToggle.addEventListener('change', () => {
            bindCountry('shipping_country');
        });
    }
}
